import { day } from '~/astra'
import { PlanningNoteAnalysisResponse } from '~/clients/derived-api-types'
import { Surgery } from '~/storm'
import { isScheduledSurgery } from '~/utils/dips'
import { isNotNullish, isNullish } from '~/utils/guards'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'

import { SurgeryMetadataArgs } from '../../saveSurgeryMetadata'
import { AvailabilityPeriod, getAvailabilityLabel, isSameAvailabilityPeriod } from './suggestionFormatting'

export type TagAction = 'add' | 'remove'

export type TagData = {
    id: keyof PlanningNoteAnalysisResponse
    label: string
    action: TagAction
}

function hasSameAvailabilityPeriod(period: AvailabilityPeriod, periods: AvailabilityPeriod[]): boolean {
    return periods.some(currentPeriod => isSameAvailabilityPeriod(period, currentPeriod))
}

function hasDifferentAvailabilityPeriods(suggested: AvailabilityPeriod[], current: AvailabilityPeriod[]): boolean {
    if (suggested.length !== current.length) return true
    return suggested.some(period => !hasSameAvailabilityPeriod(period, current))
}

export function convertToValidSuggestions(suggestions: Partial<PlanningNoteAnalysisResponse>): SurgeryMetadataArgs {
    const result: SurgeryMetadataArgs = {
        isPriority: suggestions.patient_prioritized ?? undefined,
        isShortNotice: suggestions.patient_short_notice ?? undefined,
        isCancerSurgery: suggestions.surgery_cancer ?? undefined,
        isConfirmed: suggestions.patient_confirmed ?? undefined,
    }

    if (suggestions.patient_ready === false) {
        result.isCleared = false
    } else if (suggestions.patient_ready === true) {
        result.isCleared = true
    }

    if (suggestions.is_waiting === true) {
        result.isWaiting = true
    }

    if (isNotNullish(suggestions.self_declaration_form)) {
        result.selfDeclarationFormNeedsFollowUp = suggestions.self_declaration_form
    }

    if (isNotNullish(suggestions.external_document)) {
        result.externalDocumentNeedsFollowUp = suggestions.external_document
    }

    if (isNotNullish(suggestions.test_results)) {
        result.testResultsNeedsFollowUp = suggestions.test_results
    }

    if (suggestions.availability_periods && suggestions.availability_periods.length > 0) {
        const patientAvailability = suggestions.availability_periods
            .map(period => {
                if (period.type === 'available_from' && 'start_date' in period) {
                    return {
                        type: 'availableFrom' as const,
                        startDate: day(period.start_date),
                    }
                }

                if ('start_date' in period && 'end_date' in period) {
                    return {
                        type: 'unavailableBetween' as const,
                        startDate: day(period.start_date),
                        endDate: day(period.end_date),
                    }
                }

                return undefined
            })
            .filter((period): period is NonNullable<typeof period> => period !== undefined)

        if (patientAvailability.length > 0) {
            result.patientAvailability = patientAvailability
        }
    }

    return result
}

function getBooleanTagAction(suggested: boolean | null | undefined, current: boolean | null | undefined): TagAction | null {
    if (isNullish(suggested)) return null
    if (suggested === current) return null
    return suggested ? 'add' : 'remove'
}

function maybeBooleanTag({
    id,
    label,
    suggested,
    current,
}: {
    id: keyof PlanningNoteAnalysisResponse
    label: string
    suggested: boolean | null | undefined
    current: boolean | null | undefined
}): TagData | null {
    const action = getBooleanTagAction(suggested, current)
    return action ? { id, label, action } : null
}

export function buildAnalysisTags({
    suggestions,
    surgery,
    showSurgeryCancerSuggestions = true,
}: {
    suggestions: Partial<PlanningNoteAnalysisResponse>
    surgery: Surgery
    showSurgeryCancerSuggestions?: boolean
}): TagData[] {
    const tags: TagData[] = []
    const surgeryMetadata = getSurgeryMetadata(surgery)

    const booleanDefs: Array<{
        id: keyof PlanningNoteAnalysisResponse
        label: string
        suggested: boolean | null | undefined
        current: boolean | null | undefined
    }> = [
        {
            id: 'patient_ready',
            label: 'Klarert',
            suggested: suggestions.patient_ready,
            current: surgeryMetadata?.patient_ready,
        },
        {
            id: 'patient_prioritized',
            label: 'Prioritert',
            suggested: suggestions.patient_prioritized,
            current: surgeryMetadata?.patient_prioritized,
        },
        {
            id: 'patient_short_notice',
            label: 'Kort varsel',
            suggested: suggestions.patient_short_notice,
            current: surgeryMetadata?.patient_short_notice,
        },
        ...(isScheduledSurgery(surgery)
            ? [
                  {
                      id: 'patient_confirmed' as const,
                      label: 'Bekreftet oppmøte',
                      suggested: suggestions.patient_confirmed,
                      current: surgeryMetadata?.patient_confirmed,
                  },
              ]
            : []),
        {
            id: 'is_waiting',
            label: 'Avvent',
            suggested: suggestions.is_waiting,
            current: surgeryMetadata?.availability_periods?.some(period => period.type === 'waiting') ?? null,
        },
        {
            id: 'self_declaration_form',
            label: 'EES',
            suggested: suggestions.self_declaration_form,
            current: surgeryMetadata?.self_declaration_form,
        },
        {
            id: 'external_document',
            label: 'Ekstern dok.',
            suggested: suggestions.external_document,
            current: surgeryMetadata?.external_document,
        },
        {
            id: 'test_results',
            label: 'Prøver',
            suggested: suggestions.test_results,
            current: surgeryMetadata?.test_results,
        },
    ]

    if (showSurgeryCancerSuggestions) {
        booleanDefs.push({
            id: 'surgery_cancer',
            label: 'Kreftoperasjon',
            suggested: suggestions.surgery_cancer,
            current: surgeryMetadata?.surgery_cancer,
        })
    }

    for (const def of booleanDefs) {
        const tag = maybeBooleanTag(def)
        if (tag) tags.push(tag)
    }

    const suggestionPeriods = (suggestions.availability_periods ?? []) as AvailabilityPeriod[]
    const currentPeriods = (surgeryMetadata?.availability_periods ?? []) as AvailabilityPeriod[]

    for (const suggestionPeriod of suggestionPeriods) {
        if (!hasSameAvailabilityPeriod(suggestionPeriod, currentPeriods)) {
            tags.push({
                id: 'availability_periods',
                label: getAvailabilityLabel(suggestionPeriod),
                action: 'add',
            })
        }
    }

    return tags
}

export function hasNewSuggestions({ suggestions, surgery }: { suggestions: Partial<PlanningNoteAnalysisResponse>; surgery: Surgery }): boolean {
    const surgeryMetadata = getSurgeryMetadata(surgery)

    // Booleans: review needed when a suggestion exists and differs from current value.
    if (getBooleanTagAction(suggestions.patient_ready, surgeryMetadata?.patient_ready) !== null) return true
    if (getBooleanTagAction(suggestions.patient_prioritized, surgeryMetadata?.patient_prioritized) !== null) return true
    if (getBooleanTagAction(suggestions.patient_short_notice, surgeryMetadata?.patient_short_notice) !== null) return true
    if (isScheduledSurgery(surgery) && getBooleanTagAction(suggestions.patient_confirmed, surgeryMetadata?.patient_confirmed) !== null) return true

    const currentIsWaiting = surgeryMetadata?.availability_periods?.some(period => period.type === 'waiting') ?? null
    if (getBooleanTagAction(suggestions.is_waiting, currentIsWaiting) !== null) return true

    if (getBooleanTagAction(suggestions.self_declaration_form, surgeryMetadata?.self_declaration_form) !== null) return true
    if (getBooleanTagAction(suggestions.external_document, surgeryMetadata?.external_document) !== null) return true
    if (getBooleanTagAction(suggestions.test_results, surgeryMetadata?.test_results) !== null) return true

    // Patient availability: review needed when suggested periods differ from current periods.
    const suggestionPeriods = (suggestions.availability_periods ?? []) as AvailabilityPeriod[]
    const currentPeriods = (surgeryMetadata?.availability_periods ?? []) as AvailabilityPeriod[]
    if (suggestionPeriods.length > 0 && hasDifferentAvailabilityPeriods(suggestionPeriods, currentPeriods)) return true

    return false
}
